import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface SearchResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    country?: string;
    country_code?: string;
  };
}

interface SearchBarProps {
  onLocationSelect: (lat: number, lng: number, city: string, country?: string) => void;
}

export function SearchBar({ onLocationSelect }: SearchBarProps) {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (query.length < 3) {
      setResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?` +
          `q=${encodeURIComponent(query)}&` +
          `format=json&` +
          `limit=5&` +
          `addressdetails=1&` +
          `accept-language=${i18n.language}`
        );
        const data = await response.json();
        setResults(data);
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [query, i18n.language]);

  const handleSelect = (result: SearchResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    const name = result.display_name.split(',')[0]; // First part only (city)

    // Extract country from address or display_name
    let country: string | undefined;
    if (result.address?.country) {
      country = result.address.country;
    } else {
      // Fallback: try to extract from display_name (usually last part)
      const parts = result.display_name.split(',').map(s => s.trim());
      if (parts.length > 1) {
        country = parts[parts.length - 1]; // Last part is usually country
      }
    }

    onLocationSelect(lat, lng, name, country);
    setQuery(name);
    setShowResults(false);
  };

  const handleGPSLocation = () => {
    if ('geolocation' in navigator) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          // Try to get location name from reverse geocoding
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?` +
              `lat=${lat}&lon=${lng}&` +
              `format=json&` +
              `addressdetails=1&` +
              `accept-language=${i18n.language}`
            );
            const data = await response.json();
            const city = data.address?.city || data.address?.town || data.address?.village || 'Current Location';
            const country = data.address?.country;
            onLocationSelect(lat, lng, city, country);
            setQuery(city);
          } catch (error) {
            console.error('Reverse geocoding error:', error);
            onLocationSelect(lat, lng, 'Current Location');
            setQuery('Current Location');
          }
          setLoading(false);
        },
        (error) => {
          console.error('GPS error:', error);
          // GPS error messages - keeping in English for technical errors
          alert('Failed to get your location. Please check GPS permissions.');
          setLoading(false);
        }
      );
    } else {
      alert('Your browser does not support GPS location.');
    }
  };

  return (
    <div ref={searchRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setShowResults(true)}
          placeholder={t('search.placeholder')}
          className="w-full p-3 pl-10 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground"
        />
        <div className="absolute left-3 top-3.5 text-muted-foreground">
          🔍
        </div>
        {loading && (
          <div className="absolute right-3 top-3.5">
            <div className="animate-spin h-5 w-5 border-2 border-foreground border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {/* GPS Location Button - Mapiful style */}
      <div className="mt-2 text-sm text-muted-foreground">
        {t('search.or')}{' '}
        <button
          onClick={handleGPSLocation}
          className="text-foreground underline hover:text-muted-foreground inline-flex items-center gap-1"
        >
          {t('search.useCurrentPosition')}
          <svg className="w-4 h-4" viewBox="0 0 1024 1024" fill="currentColor">
            <path d="M512 896a384 384 0 1 0 0-768a384 384 0 0 0 0 768zm0 64a448 448 0 1 1 0-896a448 448 0 0 1 0 896z" />
            <path d="M512 96a32 32 0 0 1 32 32v192a32 32 0 0 1-64 0V128a32 32 0 0 1 32-32zm0 576a32 32 0 0 1 32 32v192a32 32 0 1 1-64 0V704a32 32 0 0 1 32-32zM96 512a32 32 0 0 1 32-32h192a32 32 0 0 1 0 64H128a32 32 0 0 1-32-32zm576 0a32 32 0 0 1 32-32h192a32 32 0 1 1 0 64H704a32 32 0 0 1-32-32z" />
          </svg>
        </button>
      </div>

      {/* Results dropdown */}
      {showResults && results.length > 0 && (
        <div className="absolute z-20 w-full mt-2 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.map((result) => (
            <button
              key={result.place_id}
              onClick={() => handleSelect(result)}
              className="w-full text-left px-4 py-3 hover:bg-muted border-b border-border last:border-b-0 text-foreground transition-colors"
            >
              <p className="font-medium text-sm text-foreground">{result.display_name.split(',')[0]}</p>
              <p className="text-xs text-muted-foreground truncate">{result.display_name}</p>
            </button>
          ))}
        </div>
      )}

      {/* No results */}
      {showResults && query.length >= 3 && !loading && results.length === 0 && (
        <div className="absolute z-20 w-full mt-2 bg-card border border-border rounded-lg shadow-lg p-4">
          <p className="text-sm text-muted-foreground">{t('search.noResults')}</p>
        </div>
      )}
    </div>
  );
}





